import { useContext, useEffect, useState } from 'react'
import { View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'

import { BuyOrderStatus } from '@/constants/common'
import { USER_GET_SELL_ORDER_BY_INTERNAL_KEY } from '@/constants/graphql/queries'
import { type SellQueryResult } from '@/types/common'
import { getGraphQlErrorCode } from '@/utils/graphqlError'
import { Spinner } from '@/components/icon'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NotFoundScreen from '@/components/NotFoundScreen'
import ServerErrorScreen from '@/components/ServerErrorScreen'
import { AppealRequest } from '@/components/orders/AppealRequest'
import ChildOrderAppealingScreen from '@/components/orders/child/AppealingScreen'
import { ChildOrderCanceledScreen } from '@/components/orders/child/CanceledScreen'
import { ChildOrderCancelingScreen } from '@/components/orders/child/CancelingScreen'
import { ChildOrderCompletedScreen } from '@/components/orders/child/CompletedScreen'
import { ChildOrderRevisingScreen } from '@/components/orders/child/RevisingScreen'
import { ChildOrderWaitingForCoinReleaseScreen } from '@/components/orders/child/WaitingForCoinRelease'
import { ChildOrderWaitingForPaymentScreen } from '@/components/orders/child/WaitingForPaymentScreen'
import OrderHeader from '@/components/orders/OrderHeader'
import ParentOrderScreen from '@/components/orders/ParentOrderScreen'
import { CustomerServiceContext } from '@/components/providers/CustomerService'
import { NotificationsContext } from '@/components/providers/notifications'

export default function SellOrderDetailsScreen() {
  const { t } = useTranslation()
  const { id, childId } = useLocalSearchParams()
  const loading = useLoadingIndicator()
  const [requestingAppeal, setRequestingAppeal] = useState<boolean>(false)
  const { refresh } = useContext(NotificationsContext)
  const { openChat } = useContext(CustomerServiceContext)

  // Query Order
  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = useQuery<SellQueryResult>(USER_GET_SELL_ORDER_BY_INTERNAL_KEY, {
    variables: { internalKey: id }
  })

  const order = data?.userGetSellOrderByInternalKey

  useEffect(() => {
    return () => {
      loading.stop()
    }
  }, [loading.stop])

  function handleOpenAppealModal() {
    setRequestingAppeal(true)
  }

  const completeAppeal = () => {
    refetch()
    refresh()
    setRequestingAppeal(false)
  }

  if (!order && isLoading) {
    return (
      <>
        <Stack.Screen options={{ header: () => <OrderHeader /> }} />
        <View className={'h-full items-center justify-center bg-background-secondary'}>
          <View className={'animate-spin'}>
            <Spinner className={'size-6 text-primary'} />
          </View>
        </View>
      </>
    )
  }

  if (!order && error && getGraphQlErrorCode(error) !== 2000) {
    return <ServerErrorScreen onPress={() => void refetch()} />
  }

  if (!order) {
    return <NotFoundScreen />
  }

  // Parent Sell Order
  if (!childId) {
    return (
      <>
        <Stack.Screen options={{ header: () => <OrderHeader /> }} />
        <ParentOrderScreen data={order} refetch={refetch} />
      </>
    )
  }

  // Child Sell Order
  const childSellOrder = order.pairBuyOrder.find((childOrder) => childOrder.internalKey === childId)

  if (requestingAppeal && childSellOrder) {
    return (
      <AppealRequest
        buyOrder={childSellOrder}
        isBuyer={false}
        cancelAppeal={() => {
          setRequestingAppeal(false)
        }}
        completeAppeal={completeAppeal}
      />
    )
  }

  const parentInternalKey = order.internalKey
  const canGetHelp = childSellOrder?.status === BuyOrderStatus.appealing

  return (
    <>
      <Stack.Screen
        options={{
          header: () => <OrderHeader text={canGetHelp ? t('support:contactCustomerService') : ''} onPress={canGetHelp ? openChat : undefined} />
        }}
      />
      {childSellOrder?.status === BuyOrderStatus.paired && (
        <ChildOrderWaitingForPaymentScreen
          data={childSellOrder}
          type={'sell'}
          parentInternalKey={parentInternalKey}
          parentId={order.id}
          refetch={refetch}
          requestAppeal={handleOpenAppealModal}
        />
      )}
      {childSellOrder?.status === BuyOrderStatus.paymentMade && (
        <ChildOrderWaitingForCoinReleaseScreen
          data={childSellOrder}
          type={'sell'}
          parentInternalKey={parentInternalKey}
          parentId={order.id}
          requestAppeal={handleOpenAppealModal}
          refetch={refetch}
        />
      )}
      {childSellOrder?.status === BuyOrderStatus.completed && (
        <ChildOrderCompletedScreen
          data={childSellOrder}
          type={'sell'}
          parentInternalKey={parentInternalKey}
          parentId={order.id}
        />
      )}
      {childSellOrder?.status === BuyOrderStatus.canceling && (
        <ChildOrderCancelingScreen
          data={childSellOrder}
          type={'sell'}
          parentInternalKey={parentInternalKey}
          requestAppeal={handleOpenAppealModal}
        />
      )}
      {(childSellOrder?.status === BuyOrderStatus.forceCanceled ||
        childSellOrder?.status === BuyOrderStatus.canceled) && (
        <ChildOrderCanceledScreen data={childSellOrder} type={'sell'} parentInternalKey={parentInternalKey} />
      )}
      {childSellOrder?.status === BuyOrderStatus.complainted && (
        <ChildOrderRevisingScreen
          data={childSellOrder}
          type={'sell'}
          parentInternalKey={parentInternalKey}
          refetch={refetch}
          requestAppeal={handleOpenAppealModal}
        />
      )}
      {childSellOrder?.status === BuyOrderStatus.appealing && (
        <ChildOrderAppealingScreen data={childSellOrder} type={'sell'} parentInternalKey={parentInternalKey} />
      )}
    </>
  )
}
